import { Box, Grid, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Crumbs, Title, TraxxButton } from 'app/components';
import { API } from 'app/services';
import theme from 'app/theme';
import { useAsyncTask, useRouter } from 'app/utils';
import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ActionsComp, DetailComp, InputParameter, MatchList, RenewScreeningComp, ReportPreview } from './components';

const useStyles = makeStyles(({ breakpoints, spacing }) => ({
  root: {
    padding: spacing(3, 3, 8),
    [breakpoints.down('md')]: {
      width: '100%',
      margin: 0,
    },
  },
  noPrint: {
    '@media print': {
      display: 'none',
    },
  },
  floatButton: {
    float: 'right',
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
  },
  renewBox: {
    marginTop: '15px',
  },
  footer: {
    boxShadow: '0px 4px 14px rgba(0, 0, 0, 0.15)',
  },
}));

let mounted = false;

function ScreeningDetailRegtank(props) {
  const { routeType = 'account', screenId, onNext, newPage } = props;
  const classes = useStyles();

  const [screening, setScreening] = useState(null);
  const [matchList, setMatchList] = useState();
  const [auditLog, setAuditLog] = useState();
  const [refreshKey, setRefreshKey] = useState(0); // 🔥 NEW

  const loading = useSelector((state) => state.layout.loadingTasks);
  const access = useSelector((state) => state.profile.access);
  const wallet = useSelector((state) => state.profile.wallet);

  const [runLoadScreening] = useAsyncTask('getScreening');
  const [runAuditLog] = useAsyncTask('getAuditLog');

  const router = useRouter();
  const { params } = router.match;

  const [showPreview, setShowPreview] = useState(false);
  const [confirmRenewScreen, setConfirmRenewScreen] = useState(false);
  const [renewScreeningLoading, setRenewScreeningLoading] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const screening_id = params.screening_id || screenId;

  useEffect(() => {
    mounted = true;
    return () => (mounted = false);
  }, []);

  useEffect(() => {
    if (access?.token) getScreening();
  }, [access]);

  // 🔥 MAIN FETCH
  const getUpdatedScreening = () => {
    if (!screening_id) return enqueueSnackbar('invalid id');

    runLoadScreening(async () => {
      const loader = routeType === 'account' ? API.Screening.get_update_screen : API.Admin.get_update_screen;

      const { model } = await loader(screening_id);
    });
  };

  const getScreening = () => {
    if (!screening_id) return enqueueSnackbar('invalid id');

    runLoadScreening(async () => {
      const loader = routeType === 'account' ? API.Screening.get_screen : API.Admin.get_screen;

      const { model } = await loader(screening_id);

      getAuditLog();

      if (mounted) {
        setScreening(model);

        // 🔥 ADD THIS
        if (model?.status === 'complete' && onNext) {
          onNext();
        }
      }
    });
  };

  const getAuditLog = () => {
    runAuditLog(async () => {
      const loader = routeType === 'account' ? API.Users.get_event_logs : API.Admin.get_event_logs;

      const { models } = await loader({
        owner_type: 'screen',
        owner: screening_id,
      });

      if (mounted) setAuditLog(models);
    });
  };

  // 🔥 REFRESH FUNCTION
  const refreshData = async () => {
    await getUpdatedScreening();
    await getScreening();
    setRefreshKey((prev) => prev + 1); // force MatchList reload
    enqueueSnackbar('Match list refreshed', { variant: 'success' });
  };

  const actions = [{ buttonText: 'Generate report', onPress: () => setShowPreview(true) }];

  const renewScreen = [{ buttonText: 'Renew Screening', onPress: () => setConfirmRenewScreen(true) }];

  const submitRenewScreen = async () => {
    if (wallet.balance < 400) {
      enqueueSnackbar('Insufficient wallet balance', { variant: 'error' });
      return;
    }

    setRenewScreeningLoading(true);

    const result = await API.Screening.renew_screening({
      screening_id,
      case_id: JSON.parse(screening.case_id),
    });

    if (result?.screens?.[0]) {
      setRenewScreeningLoading(false);
      setConfirmRenewScreen(false);

      enqueueSnackbar('Screening submitted successfully!', {
        variant: 'success',
      });

      router.history.push(`/screenings/${result.screen_result_id}/detail`);

      await getScreening();
      setRefreshKey((prev) => prev + 1); // 🔥 refresh matchlist
    }
  };

  return (
    <Box className={classes.root}>
      {showPreview && <ReportPreview auditLog={auditLog} cancelPreview={() => setShowPreview(false)} routeType={routeType} matchList={matchList} screening={screening} />}

      {!showPreview && (
        <>
          <Box className={classes.noPrint} display="flex" justifyContent="flex-end" style={{ gap: 12 }}>
            <TraxxButton onSubmit={refreshData}>Refresh Match List</TraxxButton>
            <TraxxButton className={classes.floatButton} onSubmit={() => setShowPreview(true)}>
              Generate report
            </TraxxButton>
          </Box>

          <Title prefix="Screening detail">Powered by Regtank</Title>

          <Grid container spacing={2}>
            <Grid item md={6} xs={12}>
              <DetailComp newPage={newPage} reload={getScreening} routeType={routeType} screening={screening} loading={loading.getScreening} />

              <ActionsComp actions={renewScreen} className={classes.renewBox} />
            </Grid>

            <Grid item md={6} xs={12}>
              <InputParameter reload={getScreening} routeType={routeType} screening={screening} loading={loading.getScreening} />
            </Grid>

            {/* 🔥 MATCH LIST */}
            <Grid item xs={12}>
              {screening?.status === 'complete' && !loading.getScreening && (
                <MatchList
                  key={refreshKey}
                  newPage={newPage}
                  routeType={routeType}
                  onLoad={setMatchList}
                  flagged={screening.no_of_flags}
                  screening_id={screening.id}
                  screen_provider_id={screening.screen_provider_id}
                  isRegtank={true}
                  screening={screening}
                />
              )}
            </Grid>
          </Grid>
        </>
      )}

      <RenewScreeningComp open={confirmRenewScreen} setOpen={setConfirmRenewScreen} loading={renewScreeningLoading} onSubmit={submitRenewScreen} />
    </Box>
  );
}

export default ScreeningDetailRegtank;
